import {IChatMessageCopilotMetaData, IChatMessageData, IChatMessageThreadData} from "@buildwithflux/models";

export type ExecutionPlanStatus =
    | "generating"
    | "processing"
    | "ready"
    | "ready_to_execute"
    | "working"
    | "completed"
    | "ambiguous"
    | "waiting_for_input"
    | "impossible"
    | "discard"
    | "failed"
    | "stopping"
    | "stopped";

/**
 * Checks if a thread is currently thinking by looking at the most recent message status.
 * Returns true if the latest copilot message has status "thinking" or "init".
 */
export function isThreadThinking<T extends IChatMessageData>(messages: {[messageUid: string]: T} | undefined): boolean {
    if (!messages) return false;

    // Get all copilot messages
    const copilotMessages = Object.values(messages).filter((msg) => msg.meta_data.type === "copilot");

    if (copilotMessages.length === 0) return false;

    // Find the most recent copilot message by created_at timestamp
    const latestMessage = copilotMessages.reduce((latest, msg) => (msg.created_at > latest.created_at ? msg : latest));

    const copilotMeta = latestMessage.meta_data as IChatMessageCopilotMetaData;
    // Check for both "thinking" and "init" states as both indicate agent is processing
    return copilotMeta.message_status === "thinking" || copilotMeta.message_status === "init";
}

/**
 * Checks if a thread is currently executing by looking at message metadata.
 * Returns true if any copilot message has action_execution_status === "executing".
 */
export function isThreadExecuting<T extends IChatMessageData>(
    messages: {[messageUid: string]: T} | undefined,
    latestBrief: IChatMessageThreadData["latest_brief"],
): boolean {
    if (!messages || !latestBrief) return false;

    // Find messages related to this brief
    return Object.values(messages).some((msg) => {
        if (msg.meta_data.type !== "copilot") return false;
        const copilotMeta = msg.meta_data as IChatMessageCopilotMetaData;
        // Check if this message is executing actions for this brief
        return copilotMeta.action_execution_status === "executing";
    });
}

/**
 * Calculate the full execution plan status for any thread.
 * This replicates the logic from useCopilotExecutionPlan but for any thread.
 * Returns the derived ExecutionPlanStatus that the UI would display.
 */
export function calculateExecutionPlanStatus<T extends IChatMessageData>(
    brief: IChatMessageThreadData["latest_brief"],
    messages: {[messageUid: string]: T} | undefined,
): ExecutionPlanStatus | undefined {
    const briefStatus = brief?.brief_status;
    if (!briefStatus) return undefined;

    const stopRequested = brief?.stopRequested;
    const isThinking = isThreadThinking(messages);
    const isExecuting = isThreadExecuting(messages, brief);

    // If stop has been requested, show stopping state
    if (stopRequested === true) return "stopping";

    // If agent is thinking and plan is ready to execute, show processing state
    if (isThinking && briefStatus === "ready_to_execute") return "processing";

    // If currently executing, override the brief status
    if (isExecuting || briefStatus === "working") return "working";

    // Otherwise use the brief status
    return briefStatus;
}

/**
 * Determines if a thread should block other threads.
 * Only the "working" state should show "working in another thread...":
 *
 * - "working" = Actively executing the plan (shows "Working...")
 *
 * Other states like "generating", "processing", "ready_to_execute" allow concurrent work.
 */
export function isThreadStatusActivelyWorking(executionPlanStatus: ExecutionPlanStatus | undefined): boolean {
    return executionPlanStatus === "working";
}
